import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Navigate, Route, Routes } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import i18n from 'i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';
import { withStyles } from '@mui/styles';
import AppBar from '@mui/material/AppBar';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import classNames from 'classnames';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PerfectScrollbar from 'react-perfect-scrollbar';

import { withKeycloak } from '@react-keycloak/web';
import { withRouter } from '../HOCs/withRouter.jsx';

import {
    addPreviousHref,
    clearCookies,
    clearLocalStorage,
    fetchUserInfo,
    finishLogIn,
    setCurrentUserPermissions
} from '../../actions';
import DrawerAdminPanel from './DrawerAdminPanel';
import DrawerDevicePanel from './DrawerDevicePanel';
import DrawerLicensePanel from './DrawerLicensePanel';
import DrawerMobileDevicePanel from './DrawerMobileDevicePanel';
import DrawerVMPanel from './DrawerVMPanel';
import Footer from './Footer';
import { drawerIconTypes } from './Header';
import ProtectedRoute from './CustomRoutes/ProtectedRoute';
import DefaultRoute from './CustomRoutes/DefaultRoute';
import DefaultAdminRoute from './CustomRoutes/DefaultAdminRoute';
import {
    fetchInfrastructure,
    setLocationsToInfrastructure,
    setWhereaboutsToInfrastructure
} from '../../actions/infrastructure';

import { calcCurrentPerms, getCookie } from '../../utils';
import DeviceManufacturersList from '../DeviceManufacturers/DeviceManufacturersList';
import DeviceAssignmentReasonsList from '../DeviceAssignmentReasons/DeviceAssignmentReasonsList';
import DrawerUserWithoutComputers from './DrawerUsersWithoutComputers';
import DrawerReportsPanel from './DrawerReportsPanel';
import DrawerReviewLicensePanel from './DrawerReviewLicensePanel';
import GeneralProgressBar from './GeneralProgressBar';
import HeaderWithRouter from './CustomRoutes/HeaderWithRouter';

const MyAssets = React.lazy(() => import('../MyAssets'));
const EmployeeAssets = React.lazy(() => import('../EmployeeAssets'));
const Device = React.lazy(() => import('../Device/DeviceList'));
const MobileDevice = React.lazy(() => import('../MobileDevice/MobileDeviceList'));
const License = React.lazy(() => import('../License/LicenseList'));
const LicensePayment = React.lazy(() => import('../License/LicensePayments/LicensePaymentList'));
const LicenseReview = React.lazy(() => import('../License/Review/Review'));
const LicenseSettings = React.lazy(() => import('../LicenseSettings'));
const VirtualMachine = React.lazy(() => import('../VirtualMachines/VMList'));
const GeneralCategoryList = React.lazy(() => import('../GeneralCategory/GeneralCategoryList'));
const DeviceType = React.lazy(() => import('../DeviceType/DeviceTypeList'));
const MobileDeviceType = React.lazy(() => import('../MobileDeviceType/MobileDeviceTypeList'));
const LicenseManufacturers = React.lazy(() => import('../LicenseManufacturers/LicenseManufacturersList'));
const ProductName = React.lazy(() => import('../LicenseTitle/LicenseProductName/LicenseProductNameList'));
const ProductVersion = React.lazy(() => import('../LicenseTitle/LicenseProductVersion/LicenseProductVersionList'));
const ProductAttribute = React.lazy(() => import('../LicenseTitle/LicenseProductAttribute/LicenseProductAttributeList'));
const MobileDeviceVersionList = React.lazy(() => import('../MobileDeviceVersion/MobileDeviceVersionList'));
const Archive = React.lazy(() => import('../Archive/ArchiveList'));
const PermissionList = React.lazy(() => import('../Permission/PermissionList'));
const DeviceAssignmentReport = React.lazy(() => import('../DeviceAssignmentReport'));
const DeviceUnassignmentReport = React.lazy(() => import('../DeviceUnassignmentReport'));
const ExpensesReport = React.lazy(() => import('../ExpenseReport'));
const UsersWithoutComputers = React.lazy(() => import('../UsersWithoutComputers'));
const OrderingReport = React.lazy(() => import('../OrderingReport'));
const EisEcReports = React.lazy(() => import('../EisEcReports'));
const LicensesPaymentsReport = React.lazy(() => import('../LicensesPaymentsReport'));

const localeMap = {
    en: 'en',
    ru: 'ru'
};

dayjs.locale(localeMap.en);

const drawerWidth = 334;

let AppHeader = HeaderWithRouter;

const DeviceWithRouter = withRouter(Device);
const MobileDeviceWithRouter = withRouter(MobileDevice);
const LicenseWithRouter = withRouter(License);
const VirtualMachineWithRouter = withRouter(VirtualMachine);
const LicensePaymentWithRouter = withRouter(LicensePayment);
const LicenseReviewWithRouter = withRouter(LicenseReview);
const UsersWithoutComputersWithRouter = withRouter(UsersWithoutComputers);

const styles = theme => ({
    appFrame: {
        zIndex: 1,
        display: 'flex',
        width: '100%',
        minHeight: '100%',
        flexDirection: 'column'
    },
    appBar: {
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    },
    appBarShift: {
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    'appBarShift-left': {
        marginLeft: drawerWidth
    },
    'appBarShift-right': {
        marginRight: drawerWidth
    },
    footer: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        fontFamily: '\'DM Sans\', Roboto, \'Fira Sans\', sans-serif',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        bottom: 0,
        fontSize: '0.8rem',
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        flex: '0 0 auto',
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        }),
        boxShadow:
            '0px -2px 4px -1px rgba(0, 0, 0, 0.05), 0px -4px 5px 0px rgba(0, 0, 0, 0.004), 0px -1px 10px 0px rgba(0, 0, 0, 0.003)',
        '& a': {
            textDecoration: 'none',
            color: '#627caf'
        }
    },
    footerShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    'footerShift-left': {
        marginLeft: drawerWidth
    },
    'footerShift-right': {
        marginRight: drawerWidth
    },
    drawerPaper: {
        width: drawerWidth,
        zIndex: 1001
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 8px',
        ...theme.mixins.toolbar
    },
    content: {
        flex: '1 0 auto',
        overflow: 'auto',
        padding: theme.spacing(2),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    },
    loginPage: {
        flex: '1 0 auto',
        overflow: 'auto',
        padding: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    'contentShift-left': {
        marginLeft: drawerWidth
    }
});

export const pages = {
    device: 'device',
    mobile: 'mobile',
    license: 'license',
    licensePayment: 'license-payment',
    myAssets: 'my-assets',
    employeeAssets: 'employee-assets',
    reviewLicense: 'review-license',
    virtual: 'vm',
    usersWithoutComputers: 'usersWithoutComputers',
    reports: {
        location: 'location',
        deviceAssignmentReport: 'deviceAssignmentReport',
        deviceUnassignmentReport: 'deviceUnassignmentReport',
        expensesReport: 'expensesReport',
        orderingReport: 'orderingReport',
        eisEcReports: 'eisEcReports',
        licensesPaymentsReport: 'licensesPaymentsReport'
    },
    admin: {
        generalCategories: 'generalCategories',
        deviceType: 'deviceType',
        deviceManufacturer: 'deviceManufacturer',
        deviceAssignmentReasons: 'deviceAssignmentReasons',
        mobileDeviceType: 'mobileDeviceType',
        licenseManufacturer: 'licenseManufacturer',
        licenseTitle: 'licenseTitle',
        licenseProductName: 'licenseProductName',
        licenseProductVersion: 'licenseProductVersion',
        licenseProductAttribute: 'licenseProductAttribute',
        mobileOSVersion: 'mobileOSVersion',
        permissions: 'permissions',
        licenseSettings: 'licenseSettings',
    }
};

export const groupPermissions = {
    Employee: {
        value: 'eis_guest',
        defaultRoute: '/my-assets',
        defaultAdminRoute: '',
        editAccess: [],
        addNewAccess: [],
        assignAccess: [],
        searchAccess: []
    },

    Group_EIS_Admin: {
        value: 'eis_admin',
        defaultRoute: '/device',
        defaultAdminRoute: '/admin/device',
        editAccess: [
            pages.device,
            pages.mobile,
            pages.license,
            pages.reviewLicense,
            pages.licensePayment,
            pages.virtual,
            pages.usersWithoutComputers,
            ...Object.values(pages.admin),
            ...Object.values(pages.reports)
        ],
        addNewAccess: [
            pages.device,
            pages.mobile,
            pages.license,
            pages.reviewLicense,
            pages.licensePayment,
            pages.virtual,
            pages.usersWithoutComputers,
            ...Object.values(pages.admin),
            ...Object.values(pages.reports)
        ],
        assignAccess: [pages.device, pages.mobile, pages.license, pages.reviewLicense, pages.licensePayment],
        searchAccess: [pages.device, pages.mobile, pages.license, pages.reviewLicense, pages.licensePayment]
    },

    Group_EIS_Reader: {
        value: 'eis_reader',
        defaultRoute: '/device',
        defaultAdminRoute: '/admin/device',
        editAccess: [],
        addNewAccess: [],
        assignAccess: [],
        searchAccess: [
            pages.device,
            pages.mobile, pages.license,
            pages.reports.orderingReport,
            pages.reports.eisEcReports,
            pages.licensePayment
        ]
    },

    Group_EIS_Device_Manager: {
        value: 'eis_devicemanager',
        defaultRoute: '/device',
        defaultAdminRoute: '/admin/device',
        editAccess: [
            pages.device,
            pages.usersWithoutComputers,
            pages.admin.deviceType,
            pages.admin.deviceManufacturer,
        ],
        addNewAccess: [
            pages.device,
            pages.usersWithoutComputers,
            pages.admin.deviceType,
            pages.admin.deviceManufacturer,
        ],
        assignAccess: [pages.device],
        searchAccess: [pages.device, pages.employeeAssets]
    },

    Group_EIS_Device_Assistant: {
        value: 'eis_deviceassistant',
        defaultRoute: '/device',
        defaultAdminRoute: '',
        editAccess: [],
        addNewAccess: [pages.device],
        assignAccess: [pages.device],
        searchAccess: [pages.device, pages.employeeAssets]
    },

    Group_EIS_Mobile_Device_Manager: {
        value: 'eis_mobiledevicemanager',
        defaultRoute: '/mobile-device',
        defaultAdminRoute: '/admin/mobile-device',
        editAccess: [pages.mobile, pages.admin.mobileDeviceType],
        addNewAccess: [pages.mobile, pages.admin.mobileDeviceType],
        assignAccess: [pages.mobile],
        searchAccess: [pages.mobile, pages.employeeAssets]
    },

    Group_EIS_Mobile_Device_Assistant: {
        value: 'eis_mobiledeviceassistant',
        defaultRoute: '/mobile-device',
        defaultAdminRoute: '',
        editAccess: [],
        addNewAccess: [pages.mobile],
        assignAccess: [pages.mobile],
        searchAccess: [pages.mobile, pages.employeeAssets]
    },

    Group_EIS_Licenses_Manager: {
        value: 'eis_licensesmanager',
        defaultRoute: '/license',
        defaultAdminRoute: '/admin/manufacturers',
        editAccess: [pages.license,
            pages.virtual,
            pages.admin.licenseManufacturer,
            pages.admin.licenseTitle,
            pages.admin.licenseProductName,
            pages.admin.licenseProductVersion,
            pages.admin.licenseProductAttribute,
            pages.reports.licensesPaymentsReport,
            pages.reviewLicense,
            pages.licensePayment],
        addNewAccess: [pages.license,
            pages.virtual,
            pages.admin.licenseManufacturer,
            pages.admin.licenseProductName,
            pages.admin.licenseProductVersion,
            pages.admin.licenseProductAttribute,
            pages.reports.licensesPaymentsReport,
            pages.reviewLicense,
            pages.licensePayment],
        assignAccess: [pages.license, pages.reviewLicense],
        searchAccess: [pages.license, pages.device, pages.reviewLicense, pages.licensePayment, pages.employeeAssets]
    },

    Group_EIS_Licenses_Assistant: {
        value: 'eis_licensesassistant',
        defaultRoute: '/license',
        defaultAdminRoute: '',
        editAccess: [pages.virtual, pages.reviewLicense],
        addNewAccess: [pages.virtual, pages.reviewLicense],
        assignAccess: [pages.license, pages.reviewLicense],
        searchAccess: [pages.license, pages.device, pages.reviewLicense, pages.employeeAssets]
    },

    Group_EIS_Virtual_Machines_Manager: {
        value: 'eis_virtualmachinesmanager',
        defaultRoute: '/virtual-machine',
        defaultAdminRoute: '',
        editAccess: [pages.license, pages.virtual],
        addNewAccess: [pages.license, pages.virtual],
        assignAccess: [pages.license],
        searchAccess: [pages.license, pages.employeeAssets]
    }
};

export const routesWithPerms = [
    {
        path: '/my-assets',
        perm: Object.values(groupPermissions)
    },
    {
        path: '/employee-assets',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Device_Manager,
            groupPermissions.Group_EIS_Device_Assistant,
            groupPermissions.Group_EIS_Mobile_Device_Manager,
            groupPermissions.Group_EIS_Mobile_Device_Assistant,
            groupPermissions.Group_EIS_Licenses_Manager,
            groupPermissions.Group_EIS_Licenses_Assistant,
            groupPermissions.Group_EIS_Virtual_Machines_Manager
        ]
    },
    {
        path: '/device',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Device_Manager,
            groupPermissions.Group_EIS_Device_Assistant,
            groupPermissions.Group_EIS_Licenses_Assistant,
            groupPermissions.Group_EIS_Licenses_Manager
        ]
    },
    {
        path: '/mobile-device',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Mobile_Device_Manager,
            groupPermissions.Group_EIS_Mobile_Device_Assistant
        ]
    },
    {
        path: '/license',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Licenses_Manager,
            groupPermissions.Group_EIS_Licenses_Assistant,
            groupPermissions.Group_EIS_Virtual_Machines_Manager
        ]
    },
    {
        path: '/license-payment',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Licenses_Manager,
            groupPermissions.Group_EIS_Licenses_Assistant,
        ]
    },
    {
        path: '/review-license',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Licenses_Manager,
            groupPermissions.Group_EIS_Licenses_Assistant,
        ]
    },
    {
        path: '/virtual-machine',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Virtual_Machines_Manager,
            groupPermissions.Group_EIS_Licenses_Manager,
            groupPermissions.Group_EIS_Licenses_Assistant
        ]
    },
    {
        path: '/reports/archive',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Device_Manager,
        ]
    },
    {
        path: '/reports/device-assignment-report',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Device_Manager,
        ]
    },
    {
        path: '/reports/device-unassignment-report',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Device_Manager,
        ]
    },
    {
        path: '/reports/expense-report',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Device_Manager,
        ]
    },
    {
        path: '/reports/ordering-report',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Device_Manager,
        ]
    },
    {
        path: '/reports/eis-ec-reports',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
        ]
    },
    {
        path: '/reports/licenses-payments-report',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Licenses_Manager,
        ]
    },
    {
        path: '/users-without-computers',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Device_Manager,
        ]
    },
    {
        path: '/admin/general-categories',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Device_Manager
        ]
    },
    {
        path: '/admin/device',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Device_Manager
        ]
    },
    {
        path: '/admin/device-manufacturer',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Device_Manager
        ]
    },
    {
        path: '/admin/device-assignment-reasons',
        perm: [
            groupPermissions.Group_EIS_Admin,
        ]
    },
    {
        path: '/admin/mobile-device',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Mobile_Device_Manager
        ]
    },
    {
        path: '/admin/manufacturers',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Licenses_Manager
        ]
    },
    {
        path: '/admin/title-license',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Licenses_Manager
        ]
    },
    {
        path: '/admin/title-license/product-name',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Licenses_Manager
        ]
    },
    {
        path: '/admin/title-license/product-version',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Licenses_Manager
        ]
    },
    {
        path: '/admin/title-license/product-attribute',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Licenses_Manager
        ]
    },
    {
        path: '/admin/archive',
        perm: [groupPermissions.Group_EIS_Admin, groupPermissions.Group_EIS_Reader]
    },
    {
        path: '/admin/mobile-versions',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Reader,
            groupPermissions.Group_EIS_Mobile_Device_Manager
        ]
    },
    {
        path: '/admin/permissions',
        perm: [groupPermissions.Group_EIS_Admin]
    },
    {
        path: '/admin/expense-report',
        perm: [
            groupPermissions.Group_EIS_Admin,
            groupPermissions.Group_EIS_Device_Manager,
            groupPermissions.Group_EIS_Mobile_Device_Manager
        ]
    },
    {
        path: '/admin/settings/licenses',
        perm: [
            groupPermissions.Group_EIS_Admin,
        ]
    }

];

export const MAX_EXPENSE_ONE_ENTITY = 1e6;

class Main extends PureComponent {

    constructor(props) {
        super(props);
        this.state = {
            currentLocale: getCookie('Exadel.EIS.Language') || props.currLanguage,
            open: false
        };
        this.changeLocale(getCookie('Exadel.EIS.Language') || props.currLanguage);
    }

    componentDidMount() {
        this.syncLogout = event => {
            if (event.key === 'logout') {
                clearLocalStorage();
                clearCookies();
                this.props.history.replace('/');
                this.props.keycloak.logout();
            }
        };

        window.addEventListener('storage', this.syncLogout);

        return (AppHeader = (Wrapped => props =>
            (
                <Wrapped
                    {...props}
                    isDrawerOpened={this.state.open}
                    onDrawerOpen={this.handleDrawerOpen}
                    onDrawerClose={this.handleDrawerClose}
                />
            )
        )(HeaderWithRouter));
    }

    componentWillUnmount() {
        window.removeEventListener('storage', this.syncLogout);
    }

    changeLocale = locale => {
        dayjs.locale(locale);
        i18n.changeLanguage(locale);
    }

    async componentDidUpdate(prevProps) {
        const prevLangCode = prevProps.currLanguage;
        const curLangCode = this.props.currLanguage;
        const prevPathname = prevProps.location?.pathname || '';
        const curPathname = this.props.location?.pathname || '';
        const {
            keycloak,
            finishLogIn,
            fetchInfrastructure,
            setLocationsToInfrastructure,
            setWhereaboutsToInfrastructure,
            setCurrentUserPermissions,
            fetchUserInfo,
        } = this.props;

        if (prevLangCode !== curLangCode) {
            this.setState({ currentLocale: curLangCode });
            this.changeLocale(curLangCode);
        }

        const isUrl = curPathname === '/license-payment' || curPathname === '/my-assets' || curPathname === '/employee-assets';

        if (isUrl) prevPathname !== curPathname && this.handleDrawerClose();

        if (this.props.keycloakInitialized && prevProps.keycloakInitialized !== this.props.keycloakInitialized) {
            const permissions = await keycloak.tokenParsed.resource_access['Exadel Inventory System'].roles;
            const userName = await keycloak.tokenParsed.preferred_username;

            setCurrentUserPermissions(calcCurrentPerms(permissions));
            finishLogIn({ userName, permissions });
            fetchUserInfo(userName);
            fetchInfrastructure();
            setLocationsToInfrastructure();
            setWhereaboutsToInfrastructure();
        }
    }

    handleDrawerOpen = () => {
        this.setState({ open: true });
    };

    handleDrawerClose = () => {
        this.setState({ open: false });
    };

    onCheckAppliedFilters() {
        const currPathname = this.props.location?.pathname || '/';

        switch (currPathname) {
            case '/device':
                return this.props.isAppliedDeviceFilters;
            case '/mobile-device':
                return this.props.isAppliedMobileDeviceFilters;
            case '/license':
                return this.props.isAppliedLicenseFilters;
            case '/review-license':
                return this.props.isAppliedReviewLicenseFilters;
            case '/virtual-machine':
                return this.props.isAppliedVMFilters;
            default: return false;
        }
    }

    render() {
        const {
            classes,
            keycloak,
            keycloakInitialized,
        } = this.props;

        const { open, currentLocale } = this.state;

        if (!keycloakInitialized) {
            return (<GeneralProgressBar />);
        }

        const isAppliedFilters = this.onCheckAppliedFilters() ? drawerIconTypes.FilterIcon : drawerIconTypes.MenuIcon;

        const drawer = (
            <Drawer
                variant="persistent"
                anchor="left"
                open={open}
                classes={{
                    paper: classes.drawerPaper
                }}
            >
                <div className={classes.drawerHeader}>
                    <IconButton
                        onClick={this.handleDrawerClose}
                        size="large"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                </div>
                <Divider />
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={currentLocale}
                >
                    <PerfectScrollbar>
                        <Routes>
                            <Route
                                path="/admin/*"
                                element={<DrawerAdminPanel />}
                            />
                            <Route
                                path="/reports/*"
                                element={<DrawerReportsPanel />}
                            />
                            <Route
                                path="/device/*"
                                element={<DrawerDevicePanel />}
                            />
                            <Route
                                path="/mobile-device/*"
                                element={<DrawerMobileDevicePanel />}
                            />
                            <Route
                                path="/license/*"
                                element={<DrawerLicensePanel />}
                            />
                            <Route
                                path="/review-license/*"
                                element={<DrawerReviewLicensePanel />}
                            />
                            <Route
                                path="/virtual-machine/*"
                                element={<DrawerVMPanel />}
                            />
                            <Route
                                path="/users-without-computers/*"
                                element={<DrawerUserWithoutComputers />}
                            />
                            <Route
                                path="*"
                                element={null}
                            />
                        </Routes>
                    </PerfectScrollbar>
                </LocalizationProvider>
            </Drawer>
        );

        return (
            keycloak.authenticated ? (
                <div className={classes.appFrame}>
                    <AppBar
                        position="static"
                        className={classNames(classes.appBar, {
                            [classes.appBarShift]: open,
                            [classes[`appBarShift-left`]]: open
                        })}
                    >
                        <Routes>
                            <Route
                                path="/my-assets"
                                element={(
                                    <AppHeader
                                        drawerIconType={drawerIconTypes.MenuIcon}
                                        hideDrawerIcon
                                    />
                                )}
                            />
                            <Route
                                path="/employee-assets"
                                element={(
                                    <AppHeader
                                        drawerIconType={drawerIconTypes.MenuIcon}
                                        hideDrawerIcon
                                    />
                                )}
                            />
                            <Route
                                path="/license-payment"
                                element={(
                                    <AppHeader
                                        drawerIconType={drawerIconTypes.MenuIcon}
                                        hideDrawerIcon
                                    />
                                )}
                            />
                            <Route
                                path="/admin"
                                element={(
                                    <HeaderWithRouter
                                        isDrawerOpened={this.state.open}
                                        onDrawerOpen={this.handleDrawerOpen}
                                        onDrawerClose={this.handleDrawerClose}
                                        drawerIconType={drawerIconTypes.MenuIcon}
                                    />
                                )}
                            />
                            <Route
                                path="/*"
                                element={(
                                    <HeaderWithRouter
                                        isDrawerOpened={this.state.open}
                                        onDrawerOpen={this.handleDrawerOpen}
                                        onDrawerClose={this.handleDrawerClose}
                                        drawerIconType={isAppliedFilters}
                                    />
                                )}
                            />
                        </Routes>
                    </AppBar>

                    {drawer}

                    <main
                        className={classNames(classes.content, {
                            [classes.contentShift]: open,
                            [classes[`contentShift-left`]]: open
                        })}
                    >
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={currentLocale}
                        >
                            <React.Suspense fallback={<GeneralProgressBar />}>
                                <Routes>
                                    <Route
                                        path="/my-assets"
                                        element={(
                                            <ProtectedRoute pathKey="/my-assets">
                                                <MyAssets />
                                            </ProtectedRoute>
                                        )}
                                    />
                                    <Route
                                        path="/employee-assets"
                                        element={(
                                            <ProtectedRoute pathKey="/employee-assets">
                                                <EmployeeAssets />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/device"
                                        element={(
                                            <ProtectedRoute pathKey="/device">
                                                <DeviceWithRouter
                                                    isDrawerOpened={open}
                                                    onDrawerOpen={this.handleDrawerOpen}
                                                    actionGroup
                                                />
                                            </ProtectedRoute>
                                        )}
                                    />
                                    <Route
                                        path="/mobile-device"
                                        element={(
                                            <ProtectedRoute pathKey="/mobile-device">
                                                <MobileDeviceWithRouter
                                                    isDrawerOpened={open}
                                                    onDrawerOpen={this.handleDrawerOpen}
                                                    actionGroup
                                                />
                                            </ProtectedRoute>
                                        )}
                                    />
                                    <Route
                                        path="/license"
                                        element={(
                                            <ProtectedRoute pathKey="/license">
                                                <LicenseWithRouter
                                                    isDrawerOpened={open}
                                                    onDrawerOpen={this.handleDrawerOpen}
                                                />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/license-payment"
                                        element={(
                                            <ProtectedRoute pathKey="/license-payment">
                                                <LicensePaymentWithRouter
                                                    actionGroup
                                                />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/review-license"
                                        element={(
                                            <ProtectedRoute pathKey="/review-license">
                                                <LicenseReviewWithRouter
                                                    isDrawerOpened={open}
                                                    onDrawerOpen={this.handleDrawerOpen}
                                                />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/virtual-machine"
                                        element={(
                                            <ProtectedRoute pathKey="/virtual-machine">
                                                <VirtualMachineWithRouter
                                                    isDrawerOpened={open}
                                                    onDrawerOpen={this.handleDrawerOpen}
                                                />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/reports/archive"
                                        element={(
                                            <ProtectedRoute pathKey="/reports/archive">
                                                <Archive />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/reports/device-assignment-report"
                                        element={(
                                            <ProtectedRoute pathKey="/reports/device-assignment-report">
                                                <DeviceAssignmentReport />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/reports/device-unassignment-report"
                                        element={(
                                            <ProtectedRoute pathKey="/reports/device-unassignment-report">
                                                <DeviceUnassignmentReport />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/reports/expense-report"
                                        element={(
                                            <ProtectedRoute pathKey="/reports/expense-report">
                                                <ExpensesReport />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/reports/ordering-report"
                                        element={(
                                            <ProtectedRoute pathKey="/reports/ordering-report">
                                                <OrderingReport />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/reports/eis-ec-reports"
                                        element={(
                                            <ProtectedRoute pathKey="/reports/eis-ec-reports">
                                                <EisEcReports />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/reports/licenses-payments-report"
                                        element={(
                                            <ProtectedRoute pathKey="/reports/licenses-payments-report">
                                                <LicensesPaymentsReport />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/users-without-computers"
                                        element={(
                                            <ProtectedRoute pathKey="/users-without-computers">
                                                <UsersWithoutComputersWithRouter
                                                    isDrawerOpened={open}
                                                    onDrawerOpen={this.handleDrawerOpen}
                                                />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/device"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/device">
                                                <DeviceType />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/device-manufacturer"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/device-manufacturer">
                                                <DeviceManufacturersList />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/device-assignment-reasons"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/device-assignment-reasons">
                                                <DeviceAssignmentReasonsList />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/general-categories"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/general-categories">
                                                <GeneralCategoryList />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/mobile-device"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/mobile-device">
                                                <MobileDeviceType />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/manufacturers"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/manufacturers">
                                                <LicenseManufacturers />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/title-license/product-name"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/title-license/product-name">
                                                <ProductName />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/title-license/product-version"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/title-license/product-version">
                                                <ProductVersion />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/title-license/product-attribute"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/title-license/product-attribute">
                                                <ProductAttribute />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/mobile-versions"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/mobile-versions">
                                                <MobileDeviceVersionList />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/permissions"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/permissions">
                                                <PermissionList />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/admin/settings/licenses"
                                        element={(
                                            <ProtectedRoute pathKey="/admin/settings/licenses">
                                                <LicenseSettings />
                                            </ProtectedRoute>
                                        )}
                                    />

                                    <Route
                                        path="/reports"
                                        element={(
                                            <Navigate
                                                to="/reports/archive"
                                                replace
                                            />
                                        )}
                                    />

                                    <Route
                                        path="/admin"
                                        element={<DefaultAdminRoute />}
                                    />

                                    <Route
                                        path="/*"
                                        element={
                                            (<DefaultRoute perms={keycloak.tokenParsed.resource_access['Exadel Inventory System'].roles} />)
                                        }
                                    />
                                </Routes>
                            </React.Suspense>

                        </LocalizationProvider>
                    </main>

                    <Footer
                        className={classNames(classes.footer, {
                            [classes.footerShift]: open,
                            [classes[`footerShift-left`]]: open
                        })}
                    />
                </div>
            ) : (<GeneralProgressBar />)
        );
    }
}

Main.propTypes = {
    activeLanguage: PropTypes.shape({
        code: PropTypes.string,
    }),
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }),
    classes: PropTypes.shape({
        drawerPaper: PropTypes.string,
        drawerHeader: PropTypes.string,
        appFrame: PropTypes.string,
        appBar: PropTypes.string,
        appBarShift: PropTypes.string,
        content: PropTypes.string,
        contentShift: PropTypes.string,
        footer: PropTypes.string,
        footerShift: PropTypes.string,
    }),
    initialize: PropTypes.func,
    perms: PropTypes.array,
};

const mapStateToProps =
    ({ devicesFilters,
        licensesFilters,
        reviewLicenseFilters,
        mobileDevicesFilters,
        VMFilters,
        language }) => {
        const isAppliedDeviceFilters = devicesFilters.appliedFilters.isAppliedFilter;
        const isAppliedMobileDeviceFilters = mobileDevicesFilters.appliedFilters.isAppliedFilter;
        const isAppliedLicenseFilters = licensesFilters.appliedFilters.isAppliedFilter;
        const isAppliedReviewLicenseFilters = reviewLicenseFilters.appliedFilters.isAppliedFilter;
        const isAppliedVMFilters = VMFilters.appliedFilters.isAppliedFilter;
        return { isAppliedDeviceFilters,
            isAppliedMobileDeviceFilters,
            isAppliedLicenseFilters,
            isAppliedReviewLicenseFilters,
            isAppliedVMFilters,
            currLanguage: language.currentLanguage };
    };

export default compose(
    connect(mapStateToProps, {
        setCurrentUserPermissions,
        finishLogIn,
        addPreviousHref,
        setLocationsToInfrastructure,
        setWhereaboutsToInfrastructure,
        fetchInfrastructure,
        fetchUserInfo
    }),
    withRouter,
    withStyles(styles),
    withKeycloak
)(Main);
